import React from 'react';
import Header from '../components/ui/Header';
import AuthenticationGuard from '../components/ui/AuthenticationGuard';
import Icon from '../components/AppIcon';
import Button from '../components/ui/Button';

const steps = [
  {
    title: 'Create your first habit',
    description: 'Pick a small, repeatable action you want to track each day.',
    icon: 'Target',
    actionLabel: 'Go to Habits',
    href: '/habits',
  },
  {
    title: 'Build a flashcard deck',
    description: 'Capture key ideas you want to remember and review them regularly.',
    icon: 'Brain',
    actionLabel: 'Visit Flashcards',
    href: '/flashcards',
  },
  {
    title: 'Check your progress',
    description: 'Come back after a few days to see streaks and insights take shape.',
    icon: 'TrendingUp',
    actionLabel: 'Open Progress',
    href: '/progress',
  },
];

const resources = [
  {
    title: 'Product tour',
    description: 'Walk through the core features at your own pace.',
    icon: 'Compass',
    href: 'https://docs.cogito.app/product-tour',
  },
  {
    title: 'Community tips',
    description: 'See how other learners structure their habits and study sessions.',
    icon: 'Users',
    href: 'https://community.cogito.app',
  },
  {
    title: 'Need help?',
    description: 'Reach out to the team for onboarding support or questions.',
    icon: 'MessageCircle',
    href: 'mailto:support@cogito.app',
  },
];

const GettingStartedPage = () => {
  return (
    <AuthenticationGuard isAuthenticated={true}>
      <div className="min-h-screen bg-background">
        <Header />

        <main className="pt-16">
          <div className="max-w-4xl mx-auto px-4 lg:px-6 py-10 space-y-10">
            <section className="text-center space-y-4">
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium">
                <Icon name="Sparkles" size={16} />
                <span>Getting Started</span>
              </div>
              <h1 className="text-3xl font-bold text-foreground">Welcome to Cogito</h1>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                This quick checklist helps you set up the essentials so you can learn consistently and see meaningful progress.
              </p>
            </section>

            <section className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {steps.map((step) => (
                <div key={step.title} className="bg-card rounded-xl border border-border p-6 space-y-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name={step.icon} size={24} className="text-primary" />
                  </div>
                  <div className="space-y-2">
                    <h2 className="text-lg font-semibold text-foreground">{step.title}</h2>
                    <p className="text-sm text-muted-foreground">{step.description}</p>
                  </div>
                  <Button
                    variant="outline"
                    iconName="ArrowRight"
                    className="w-full justify-start"
                    onClick={() => window.location.href = step.href}
                  >
                    {step.actionLabel}
                  </Button>
                </div>
              ))}
            </section>

            <section className="bg-muted/40 border border-dashed border-border rounded-xl p-6 space-y-4">
              <h2 className="text-lg font-semibold text-foreground">Helpful resources</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {resources.map((resource) => (
                  <button
                    key={resource.title}
                    className="bg-background rounded-lg border border-border/60 hover:border-border transition-colors p-4 text-left"
                    onClick={() => window.open(resource.href, '_blank')}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                        <Icon name={resource.icon} size={18} className="text-primary" />
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm font-semibold text-foreground">{resource.title}</p>
                        <p className="text-xs text-muted-foreground">{resource.description}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </section>

            <section className="bg-card border border-border rounded-xl p-6 space-y-4">
              <h2 className="text-lg font-semibold text-foreground">Next steps</h2>
              <ul className="text-sm text-muted-foreground space-y-2">
                <li>• Check the dashboard after a few sessions to see your streaks grow.</li>
                <li>• Visit the testing workspace for manual sanity checks while automated QA is under construction.</li>
                <li>• Keep notes on what’s working and adjust habits or decks as you learn.</li>
              </ul>
            </section>
          </div>
        </main>
      </div>
    </AuthenticationGuard>
  );
};

export default GettingStartedPage;
